import type { ReactNode } from 'react';

import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { VersionBadge } from '@/components/layout/VersionBadge';
import { usePublicSettings } from '@/lib/settings';

// Two logo variants: the standard mark has a lot of black ink that
// disappears against a dark background, so we ship a lighter
// dark-mode variant and swap with Tailwind's `dark:` modifier rather
// than tracking colour-scheme state in JS. Both <img> tags render;
// CSS hides one.
import logoLightUrl from '@/assets/partforge-red-transparent.png';
import logoDarkUrl from '@/assets/partforge-red-transparent-dark.png';

/**
 * Two-column welcome layout used by the unauthenticated routes
 * (`/login`, `/signup`, `/forgot-password`, etc).
 *
 * - Desktop: ~3/5 left = pitch + logo + feature highlights, ~2/5 right
 *   = the auth form (passed in as `children`).
 * - Narrow viewports: collapses to single-column with the pitch on
 *   top and the form below — same content, just stacked.
 *
 * The right column keeps a max-width so the form column doesn't get
 * absurdly wide on big screens. Form muscle memory is preserved: the
 * actual auth card layout inside `children` is unchanged.
 */
export function WelcomeLayout({ children }: { children: ReactNode }) {
  return (
    <div className="relative min-h-full bg-stone-50 dark:bg-stone-900">
      {/* Pre-login theme picker. The chosen value persists in
          localStorage (`partforge.theme`) so reloads and the
          authenticated UI both pick it up. */}
      <div className="absolute right-4 top-4 z-10">
        <ThemeToggle />
      </div>
      <div className="mx-auto flex min-h-screen max-w-7xl flex-col gap-8 px-6 py-10 lg:flex-row lg:items-center lg:gap-12 lg:py-16">
        <MarketingPanel />
        <div className="flex w-full justify-center lg:w-2/5 lg:flex-shrink-0">
          {children}
        </div>
      </div>
      <div className="absolute bottom-3 left-0 right-0 flex justify-center">
        <VersionBadge />
      </div>
    </div>
  );
}

function MarketingPanel() {
  const settings = usePublicSettings();
  const mode = settings.data?.signup_mode ?? 'closed';

  return (
    <div className="lg:w-3/5">
      <div className="flex items-center gap-3">
        <img
          src={logoLightUrl}
          alt="PartForge — anvil mark"
          className="h-20 w-auto sm:h-24 dark:hidden"
        />
        <img
          src={logoDarkUrl}
          alt=""
          aria-hidden
          className="hidden h-20 w-auto sm:h-24 dark:block"
        />
      </div>

      <h1 className="mt-8 text-4xl font-semibold tracking-tight text-stone-900 dark:text-stone-50 sm:text-5xl">
        Stop wrestling with spreadsheets.
        <br />
        <span className="text-forge-700 dark:text-forge-400">
          Track parts. Build BOMs.
        </span>
      </h1>

      <p className="mt-6 max-w-2xl text-lg text-stone-600 dark:text-stone-300">
        A workshop-grade parts catalog for hobbyist electronics — your
        bench inventory, your KiCad libraries, and your distributor
        data, in one place that you actually own.
      </p>

      <ul className="mt-10 grid gap-5 sm:grid-cols-2">
        <Feature
          title="A search bar that speaks engineer"
          body={
            <>
              Type{' '}
              <code className="rounded bg-stone-200 dark:bg-stone-800 px-1 font-mono text-sm">
                10k
              </code>{' '}
              and find every <code className="font-mono">10kΩ</code>,{' '}
              <code className="font-mono">10 kohm</code>, and{' '}
              <code className="font-mono">10.0k</code> resistor on the
              shelf — even the ones with messy spreadsheet imports.
            </>
          }
        />
        <Feature
          title="Parts from LCSC, DigiKey & Mouser"
          body="Paste a product URL or part number; PartForge pulls the manufacturer, MPN, package, datasheet, image, and the full price ladder. Review the diff, then save."
        />
        <Feature
          title="BOMs with frozen revisions"
          body="Define a BOM, freeze the revision, ask whether your inventory can build N boards — see exactly which parts you're short and where they live."
        />
        <Feature
          title="Yours, end to end"
          body="A single Docker container, a SQLite file, a bind mount you control. No subscriptions, no vendor lock-in, no surprise feature paywalls."
        />
      </ul>

      <p className="mt-10 max-w-2xl text-sm text-stone-500 dark:text-stone-400">
        Open source. Self-host from a Docker stack on your own
        hardware
        {mode === 'open' && (
          <>
            , or sign up for an account on this instance using the form
            to the right.
          </>
        )}
        {mode === 'request' && (
          <>
            , or use the form to the right to request an account on
            this instance — an admin reviews each request.
          </>
        )}
        {mode === 'closed' && <>.</>}
      </p>
    </div>
  );
}

/**
 * Plain centered wrapper for short-lived auth flows (password reset,
 * accept-invite). The big marketing panel is overkill for pages
 * users hit once via a one-off email link; a centered card with the
 * logo on top is the right level of brand presence.
 */
export function CenteredAuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center bg-stone-50 dark:bg-stone-900 px-4 py-12">
      <div className="absolute right-4 top-4 z-10">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center">
          <img
            src={logoLightUrl}
            alt="PartForge"
            className="h-[4.5rem] w-auto dark:hidden"
          />
          <img
            src={logoDarkUrl}
            alt=""
            aria-hidden
            className="hidden h-[4.5rem] w-auto dark:block"
          />
        </div>
        {children}
      </div>
      <div className="absolute bottom-3 left-0 right-0 flex justify-center">
        <VersionBadge />
      </div>
    </div>
  );
}


function Feature({ title, body }: { title: string; body: ReactNode }) {
  return (
    <li className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950/40 p-4">
      <h3 className="font-semibold text-stone-900 dark:text-stone-100">
        {title}
      </h3>
      <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">{body}</p>
    </li>
  );
}
